import { createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '../../client';

export const fetchSurveysData = createAsyncThunk(
  'populationHealth/fetchSurveysData',
  async ({ accountUuid }, { rejectWithValue }) => {
    try {
      // Call the surveys-data API endpoint
      const response = await client.get(`/accounts/${accountUuid}/surveys-data`);

      if (response.ok) {
        const data = response.data;

        // Transform survey types to assessment instruments format
        // Extract from data.data.result array
        const assessmentTypes = data.data?.result || [];

        // Add "All Assessments" option at the beginning
        const instruments = [
          { id: 'all', name: 'All Assessments' },
          ...assessmentTypes.map(survey => ({
            id: survey.id,
            name: survey.title,
            description: survey.description || ''
          }))
        ];

        return {
          ok: true,
          data: {
            instruments,
            rawSurveys: assessmentTypes
          },
        };
      } else {
        return rejectWithValue({
          error: {
            message: response.data?.message || 'Failed to load survey types',
          },
        });
      }
    } catch (error) {
      return rejectWithValue({
        error: {
          message: error.message || 'Failed to load survey types',
        },
      });
    }
  }
);

export const fetchPopulationMetrics = createAsyncThunk(
  'populationHealth/fetchPopulationMetrics',
  async ({ accountUuid, filters }, { rejectWithValue }) => {
    try {
      // Prepare request body according to API spec
      const requestBody = {};

      // Add optional filters
      if (filters.startDate) {
        requestBody.startDate = filters.startDate;
      }
      if (filters.endDate) {
        requestBody.endDate = filters.endDate;
      }
      if (filters.assessmentType) {
        requestBody.assessmentType = filters.assessmentType;
      }

      // Call the real API endpoint
      const response = await client.post(
        `/accounts/${accountUuid}/population-health-metrics`,
        requestBody
      );

      if (response.ok) {
        const data = response.data;

        // Transform API response to match existing component structure
        return {
          ok: true,
          data: {
            metrics: data.data.alerts
              .sort((a, b) => a.alertTagTitle.localeCompare(b.alertTagTitle))
              .map(alert => ({
                label: alert.alertTagTitle,
                count: alert.patientCount,
                percent: alert.patientPercentage,
                alertType: alert.alertType, // Keep original for future use
                patients: [], // Placeholder for patient details
              })),
            totalPatients: data.data.totalPatients,
            totalAlerts: data.data.totalAlerts,
          },
        };
      } else {
        return rejectWithValue({
          error: {
            message: response.data?.message || 'Failed to load population health metrics',
          },
        });
      }
    } catch (error) {
      return rejectWithValue({
        error: {
          message: error.message || 'Failed to load population health metrics',
        },
      });
    }
  }
);

export const fetchBaselineFollowupMetrics = createAsyncThunk(
  'populationHealth/fetchBaselineFollowupMetrics',
  async ({ accountUuid, filters }, { rejectWithValue }) => {
    try {
      // Prepare request body according to API spec
      const requestBody = {};

      // Add optional filters
      if (filters.startDate) {
        requestBody.startDate = filters.startDate;
      }
      if (filters.endDate) {
        requestBody.endDate = filters.endDate;
      }
      if (filters.assessmentType) {
        requestBody.assessmentType = filters.assessmentType;
      }

      // Call the baseline-followup API endpoint
      const response = await client.post(
        `/accounts/${accountUuid}/baseline-followup-metrics`,
        requestBody
      );

      if (response.ok) {
        const data = response.data;

        // Transform API response to match component structure for comparison mode
        return {
          ok: true,
          data: {
            metrics: data.data.alerts
              .sort((a, b) => a.alertTagTitle.localeCompare(b.alertTagTitle))
              .map(alert => ({
                label: alert.alertTagTitle,
                alertType: alert.alertType,
                // For baseline
                baselineCount: alert.baselinePatientCount,
                baselinePercent: alert.baselinePatientPercentage,
                // For followup
                followupCount: alert.followupPatientCount,
                followupPercent: alert.followupPatientPercentage,
              // Combined count for compatibility
              count: alert.followupPatientCount,
              percent: alert.followupPatientPercentage,
              patients: [], // Placeholder for patient details
            })),
            totalBaselinePatients: data.data.totalBaselinePatients,
            totalFollowupPatients: data.data.totalFollowupPatients,
            totalBaselineAlerts: data.data.totalBaselineAlerts,
            totalFollowupAlerts: data.data.totalFollowupAlerts,
            totalPatients: data.data.totalFollowupPatients, // Use followup as default total
          },
        };
      } else {
        return rejectWithValue({
          error: {
            message: response.data?.message || 'Failed to load baseline-followup metrics',
          },
        });
      }
    } catch (error) {
      return rejectWithValue({
        error: {
          message: error.message || 'Failed to load baseline-followup metrics',
        },
      });
    }
  }
);

export const exportPopulationData = createAsyncThunk(
  'populationHealth/exportPopulationData',
  async ({ accountUuid, filters, isComparisonMode }, { rejectWithValue }) => {
    try {
      // Prepare request body
      const requestBody = {};
      if (filters.startDate) requestBody.startDate = filters.startDate;
      if (filters.endDate) requestBody.endDate = filters.endDate;
      if (filters.assessmentType) requestBody.assessmentType = filters.assessmentType;

      // Choose endpoint based on mode
      const endpoint = isComparisonMode
        ? `/accounts/${accountUuid}/baseline-followup-metrics`
        : `/accounts/${accountUuid}/population-health-metrics`;

      const response = await client.post(endpoint, requestBody);

      if (response.ok) {
        const data = response.data;
        const metrics = data.data.alerts;

        // Convert to CSV format based on mode
        let headers, rows;

        if (isComparisonMode) {
          headers = ['Alert', 'Type', 'Baseline Count', 'Baseline %', 'Follow-up Count', 'Follow-up %'];
          rows = metrics.map(alert => [
            alert.alertTagTitle,
            alert.alertType,
            alert.baselinePatientCount,
            `${alert.baselinePatientPercentage}%`,
            alert.followupPatientCount,
            `${alert.followupPatientPercentage}%`,
          ]);
        } else {
          headers = ['Alert', 'Type', 'Patient Count', 'Percentage'];
          rows = metrics.map(alert => [
            alert.alertTagTitle,
            alert.alertType,
            alert.patientCount,
            `${alert.patientPercentage}%`,
          ]);
        }

        const csvContent = [headers, ...rows]
          .map(row => row.map(field => `"${field}"`).join(','))
          .join('\n');

        // Generate date range string for filename
        const getDateRangeString = () => {
          if (filters.startDate && filters.endDate) {
            const start = new Date(filters.startDate).toISOString().split('T')[0];
            const end = new Date(filters.endDate).toISOString().split('T')[0];
            return `${start}_to_${end}`;
          }
          return new Date().toISOString().split('T')[0];
        };

        // Return data directly for Redux action payload
        return {
          csvContent,
          filename: `population_health_${
            isComparisonMode ? 'comparison' : 'metrics'
          }_${filters.assessmentType || 'all'}_${
            getDateRangeString()
          }.csv`,
          totalRecords: metrics.length,
        };
      } else {
        return rejectWithValue({
          error: {
            message: response.data?.message || 'Failed to export population health data',
          },
        });
      }
    } catch (error) {
      return rejectWithValue({
        error: {
          message: error.message || 'Failed to export population health data',
        },
      });
    }
  }
);

// Fetch alert patient details
export const fetchAlertPatientDetails = createAsyncThunk(
  'populationHealth/fetchAlertPatientDetails',
  async ({ accountUuid, alertTitle, filters }, { rejectWithValue }) => {
    try {
      // Prepare request body according to API spec
      const requestBody = {
        alertTitle: alertTitle // Required field
      };

      // Add optional filters
      if (filters?.startDate) {
        requestBody.startDate = filters.startDate;
      }
      if (filters?.endDate) {
        requestBody.endDate = filters.endDate;
      }
      if (filters?.assessmentType) {
        requestBody.assessmentType = filters.assessmentType;
      }

      // Call the alert-patient-details API endpoint
      const response = await client.post(
        `/accounts/${accountUuid}/alert-patient-details`,
        requestBody
      );

      if (response.ok) {
        const data = response.data;

        // Transform API response to match component structure
        return {
          ok: true,
          data: {
            alertTitle: data.data.alertTitle,
            alertType: data.data.alertType,
            totalPatients: data.data.totalPatients,
            populationPercentage: data.data.populationPercentage,
            populationDistribution: data.data.populationDistribution,
            patients: data.data.patients.map(patient => ({
                id: patient.patientId,
                name: patient.name,
                age: patient.age,
                gender: patient.gender,
                score: patient.assessmentScore,
                assessmentDate: patient.assessmentDate,
                // Additional fields for UI
                status: patient.assessmentScore >= 20 ? 'High' :
                        patient.assessmentScore >= 10 ? 'Medium' : 'Low',
                lastAssessment: patient.assessmentDate,
              })),
          },
        };
      } else {
        return rejectWithValue({
          error: {
            message: response.data?.message || 'Failed to load patient details',
          },
        });
      }
    } catch (error) {
      return rejectWithValue({
        error: {
          message: error.message || 'Failed to load patient details',
        },
      });
    }
  }
);

// Fetch baseline vs follow-up patient details
export const fetchBaselineFollowupPatientDetails = createAsyncThunk(
  'populationHealth/fetchBaselineFollowupPatientDetails',
  async ({ accountUuid, alertTitle, filters }, { rejectWithValue }) => {
    try {
      // Prepare request body according to API spec
      const requestBody = {
        alertTitle: alertTitle // Required field
      };

      // Add optional filters
      if (filters?.startDate) {
        requestBody.startDate = filters.startDate;
      }
      if (filters?.endDate) {
        requestBody.endDate = filters.endDate;
      }
      if (filters?.assessmentType) {
        requestBody.assessmentType = filters.assessmentType;
      }

      // Call the baseline-followup-patient-details API endpoint
      const response = await client.post(
        `/accounts/${accountUuid}/baseline-followup-patient-details`,
        requestBody
      );

      if (response.ok) {
        const data = response.data;

        // Transform API response to match component structure for comparison mode
        const baselinePatients = [];
        const followupPatients = [];

        // Process each patient and categorize them
        data.data.patients.forEach(patient => {
          const patientBase = {
            id: patient.patientId,
            name: patient.name,
            age: patient.age,
            gender: patient.gender,
          };

          // Add to baseline list if has baseline date
          // if (patient.baselineDate) {
          //   baselinePatients.push({
          //     ...patientBase,
          //     score: patient.baselineScore || 0, // API may not provide scores
          //     assessmentDate: patient.baselineDate,
          //     status: patient.baselineScore >= 20 ? 'High' :
          //             patient.baselineScore >= 10 ? 'Medium' : 'Low',
          //     lastAssessment: patient.baselineDate,
          //     category: 'baseline',
          //     // Include progress info for reference
          //     progress: patient.progress,
          //     baselineDate: patient.baselineDate,
          //   });
          // }

          // Add to follow-up list if has follow-up date
          // if (patient.followupDate) {
            followupPatients.push({
              ...patientBase,
              score: patient.followupScore || 0, // API may not provide scores
              assessmentDate: patient.followupDate,
              status: patient.followupScore >= 20 ? 'High' :
                      patient.followupScore >= 10 ? 'Medium' : 'Low',
              lastAssessment: patient.followupDate,
              category: 'followup',
              // Include progress info
              progress: patient.progress,
              // Include baseline score for comparison
              baselineScore: patient.baselineScore || 0,
              baselineDate: patient.baselineDate,
              followupDate: patient.followupDate,
              changeDirection: patient.progress?.direction,
              changePercent: patient.progress?.percentageChange,
            });
          // }
        });

        return {
          ok: true,
          data: {
            alertTitle: data.data.alertTitle,
            alertType: data.data.alertType,
            baselinePatients: data.data.baselinePatients,
            followupPatients: data.data.followupPatients,
            change: data.data.change,
            // Combined patients list with categories
            patients: data.data.patients,
            // Separated lists for tabs
            baselinePatientsList: baselinePatients,
            followupPatientsList: followupPatients,
          },
        };
      } else {
        return rejectWithValue({
          error: {
            message: response.data?.message || 'Failed to load baseline-followup patient details',
          },
        });
      }
    } catch (error) {
      return rejectWithValue({
        error: {
          message: error.message || 'Failed to load baseline-followup patient details',
        },
      });
    }
  }
);
